<template>
  <div>
    <PageTitle title="Application" />
    <b-container fluid>
      <b-row class="first-row">
        <b-col xs="12" lg="6" xl="5" class="mb-3" order="0" order-lg="1">
          <QueryList class="fill-height" />
        </b-col>

        <b-col xs="12" lg="6" xl="7" class="mb-3" order="1" order-lg="0">
          <QueryEditor class="fill-height" />
        </b-col>
      </b-row>

      <b-row>
        <!-- <b&#45;col class="mb&#45;3" cols="6"> -->
        <b-col class="mb-3">
          <Card
            title="Application"
            :error="writeError"
            errorTitle="Create Application Failed"
          >
            <div v-if="!writeAppId">
              <b-row>
                <b-col xs="12" md="4">
                  <b-form-group label="Name" label-for="name">
                    <b-form-input
                      id="name"
                      v-model="name"
                      type="text"
                      :disabled="!enabled"
                      required
                      size="sm"
                    ></b-form-input>
                  </b-form-group>
                </b-col>

                <b-col xs="12" md="8">
                  <b-form-group label="Generate">
                    <b-form-checkbox-group
                      v-model="generate"
                      :options="generateOptions"
                      switches
                      :disabled="!enabled"
                    ></b-form-checkbox-group>
                    <b-form-checkbox
                      class="fill-migration-config"
                      v-model="fillMigration"
                      switch
                      v-b-tooltip.hover.left
                      title="Data from created app will be prefilled for migration"
                      :disabled="!enabled"
                    >
                      Migration Configuration
                    </b-form-checkbox>
                  </b-form-group>
                </b-col>
              </b-row>

              <div v-if="fields.length">
                <h6>Fields</h6>
                <b-row class="pr-5 d-none d-md-flex">
                  <b-col xs="12" md="4">
                    Legend
                  </b-col>
                  <b-col xs="12" md="4">
                    Store As
                  </b-col>
                  <b-col xs="12" md="4">
                    Type
                  </b-col>
                </b-row>
                <AisonApplicationFieldFormRow
                  v-for="(item, index) in fields"
                  :key="`aison_application_field_form_row_${index}`"
                  class="my-1"
                  v-model="fields[index]"
                  :metadata="metadata[index]"
                  @remove="removeRow(index)"
                />
              </div>
            </div>

            <div v-if="writeAppId">
              App {{ writeAppId }} has been generated. Available at
              <a :href="appEditorUrl">{{ appEditorUrl }}</a>
            </div>

            <template #footer>
              <div v-if="fields.length || writeAppId">
                <div
                  class="create-app-footer"
                  v-if="fields.length && !writeAppId"
                >
                  <Button
                    :enabled="!writeLoading"
                    @click="addFieldRow"
                    variant="info"
                  >
                    Add Field
                  </Button>
                  <Button
                    :loading="writeLoading"
                    :enabled="true"
                    @click="write"
                    variant="success"
                    class="ml-2"
                  >
                    Create App
                  </Button>
                </div>
                <Button
                  v-if="writeAppId"
                  :enabled="true"
                  @click="writeAppId = null"
                  variant="success"
                >
                  Done
                </Button>
              </div>
            </template>
          </Card>
        </b-col>
      </b-row>

      <b-row>
        <b-col>
          <QueryResult />
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script lang="ts">
/* eslint-disable */
import { defineComponent } from 'vue';
import { mapGetters, mapState } from 'vuex';

import {
  AisonFieldType,
  AisonApplicationFieldCreateData,
} from '@aison/migrator/lib/adapters';
import * as generators from '@aison/migrator/lib/generators';
import { aison } from '@/api';
import store from '@/plugins/vuex';

import AisonApplicationFieldFormRow from '@/components/generators/AisonApplicationFieldFormRow.vue';
import Button from '@/components/Button.vue';
import Card from '@/components/Card.vue';
import PageTitle from '@/components/PageTitle.vue';
import QueryEditor from '@/components/source-queries/QueryEditor.vue';
import QueryList from '@/components/source-queries/QueryList.vue';
import QueryResult from '@/components/source-queries/QueryResult.vue';

export default defineComponent({
  name: 'AisonApplicationConfiguration',
  components: {
    AisonApplicationFieldFormRow,
    Button,
    Card,
    PageTitle,
    QueryEditor,
    QueryList,
    QueryResult,
  },
  data() {
    return {
      generateOptions: [
        { text: 'Summary', value: 'summary' },
        { text: 'Layout', value: 'layout' },
      ],
      // App data
      name: '',
      generate: ['summary', 'layout'],
      fields: [] as AisonApplicationFieldCreateData[],
      fillMigration: true,
      // queryResult: [] as any[],
      generator: new generators.AisonApplicationGenerator(),
      metadata: [] as generators.AisonApplicationFieldResult[],
      // Writing
      writeLoading: false,
      writeError: null,
      writeAppId: null as string | null,
    };
  },
  mounted() {
    if (this.queryResult?.length) {
      this.generateFields();
    }
  },
  computed: {
    ...mapState({
      targetAppIdSet: (state: any) => !!state.target.appId,
    }),
    ...mapGetters({
      sourceRequest: 'source/connectRequest',
      targetRequest: 'target/connectRequest',
      queryResult: 'read/flatReadResult',
    }),
    writeOptions(): any {
      return {
        name: this.name,
        summary: this.generate.includes('summary'),
        layout: this.generate.includes('layout'),
        fields: this.fields,
      };
    },
    enabled(): boolean {
      return !this.writeLoading && !this.writeAppId;
    },
    appEditorUrl(): string | null {
      if (!this.writeAppId) {
        return null;
      }
      return `${this.targetRequest.adapter.url}/#/edit/${this.writeAppId}/common/fields`;
    },
  },
  methods: {
    async write() {
      // Check if user want's to populate migrate options
      if (this.targetAppIdSet) {
        const overwrite = confirm(
          'There is an existing migration configuration, do you want to overwrite it?'
        );
        if (!overwrite) {
          return;
        }
      }

      // Write app
      try {
        this.writeLoading = true;
        const result = await aison.write({
          ...this.targetRequest,
          write: this.writeOptions,
        });
        const { id, view_ids } = result;
        this.writeAppId = `${id}@${view_ids[0]}`;
        this.writeError = null;
        if (this.fillMigration) {
          // If user has opted to fill migration generate some config values
          await this.generateMigrationConfig();
        }
      } catch (error) {
        this.writeError = error.message;
      } finally {
        this.writeLoading = false;
      }
    },
    async generateMigrationConfig() {
      const map = Object.fromEntries(
        this.fields
          .filter((f: any) => f.sourceKey)
          .map((f: any) => [f.store_as, f.sourceKey])
      );

      store.commit.target.setAppId(this.writeAppId);
      store.commit.transform.setOptions({ map: map });
      await store.dispatch.target.loadApp(null);
      if (store.state.target.datasets.length) {
        store.commit.target.setDataset(store.state.target.datasets[0].dataset);
      }
    },
    generateFields() {
      this.metadata = this.generator.generate(this.queryResult);
      this.fields = this.metadata.map(field => {
        let data_type = 'Text' as AisonFieldType;
        try {
          data_type = this.fieldTypeFromMetadata(field);
        } catch (error) {
          // eslint-disable-next-line
          console.error('error getting field from type', error);
        }
        return {
          data_type,
          legend: field.name,
          store_as: field.targetKey,
          sourceKey: field.sourceKey,
        };
      });
    },
    fieldTypeFromMetadata(
      data: generators.AisonApplicationFieldResult
    ): AisonFieldType {
      const types = Object.entries(data.aisonTypes).sort(
        (a: any, b: any) => b[1] - a[1]
      );
      const type = types[0][0];

      return type as AisonFieldType;
    },
    removeRow(index: number) {
      delete this.fields[index];
      delete this.metadata[index];
    },
    addFieldRow() {
      this.fields.push({
        data_type: 'Text',
        legend: '',
        store_as: '',
      });
    },
  },
  watch: {
    queryResult(val) {
      if (!val || !val.length) {
        this.metadata = [];
        this.fields = [];
      } else {
        this.generateFields();
      }
    },
  },
});
</script>
<style scoped>
.editor {
  /* flex: 0 0 auto; */
  height: auto;
  flex: 1 1 auto;
}

.create-app-footer {
  display: flex;
  justify-content: space-between;
  width: 100%;
}

.first-row {
  min-height: 250px;
}

.fill-migration-config {
  display: inline;
}
</style>
